import { useEffect } from 'react';
import { SITE_ORIGIN } from '../lib/region';
import { resolveSeoUrl } from '../lib/regionLocation';

/**
 * SEO Component for dynamic page titles and meta descriptions
 * Usage: <SEO title="Page Title" description="Page description" url="/region-relative-path" />
 * `url` is resolved by resolveSeoUrl: region-relative paths get the current region's prefix;
 * absolute URLs that already carry a region prefix are used unchanged.
 */
export default function SEO({ 
  title = "OPTRA Labs | AI Grant Platform for SMEs",
  description = "OPTRA Labs predicts your approval likelihood, prioritizes the right programs, and helps you secure non-dilutive funding from application to award.",
  keywords = "SME funding, small business funding, business funding platform, AI funding advisor, funding eligibility, application workflow, non-dilutive funding",
  image = "/favicon.png",
  url,
  robots = "index, follow",
  author = "OPTRA"
}) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const origin = window.location.origin || SITE_ORIGIN;
    const canonicalUrl = resolveSeoUrl(url, { origin, pathname: window.location.pathname });
    const absoluteImage = (() => {
      if (!image) return '';
      if (/^https?:\/\//i.test(image)) return image;
      return `${origin}${image.startsWith('/') ? image : `/${image}`}`;
    })();

    // Update document title
    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }

    const metaAuthor = document.querySelector('meta[name="author"]');
    if (metaAuthor) {
      metaAuthor.setAttribute('content', author);
    }
    
    // Update robots meta
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', robots);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', canonicalUrl);
    }
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', absoluteImage);
    }
    
    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
    
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', absoluteImage);
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', canonicalUrl);
    }
    
  }, [title, description, keywords, image, url, robots, author]);

  return null; // This component doesn't render anything
}
